import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const grimaud: ChauffeurPage = {
  slug: "grimaud",
  title: "Chauffeur privé à Grimaud – VTC golfe de Saint-Tropez",
  subtitle:
    "Chauffeur privé et VTC à Grimaud : transfert aéroport de Nice, gare de Saint-Raphaël, mise à disposition et liaison Saint-Tropez.",
  keywords: [
    "chauffeur privé grimaud",
    "vtc grimaud",
    "chauffeur grimaud",
    "transfert grimaud aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Grimaud",
    subtitle:
      "Le village médiéval perché au-dessus du golfe : transfert aéroport de Nice, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC Grimaud",
      "chauffeur village de Grimaud",
      "transfert aéroport Nice",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Grimaud (golfe de Saint-Tropez)",
    badges: [
      "Village médiéval & Port Grimaud",
      "Transfert aéroport de Nice",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Grimaud",
    items: [
      {
        title: "Village perché maîtrisé",
        description:
          "Ruelles en pente, château et parkings éloignés du centre : le chauffeur vous dépose au plus près et revient à la demande.",
      },
      {
        title: "Tout le golfe de Saint-Tropez",
        description:
          "Entre le village haut et Port Grimaud en contrebas, une desserte fluide du golfe, sans contrainte de stationnement.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l’aéroport de Nice, comptez environ 1h20 à 1h30 de route, gérées dans un confort haut de gamme.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Grimaud",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Grimaud ⇄ aéroport de Nice Côte d’Azur avec suivi de vol ; trajet plus long qu’au départ de Cannes, anticipé dès la prise en charge.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        text: "Prise en charge à la gare de Saint-Raphaël, à environ 30 à 40 minutes, comme point de transfert alternatif au train.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée pour le village, Port Grimaud, les plages et les villas du golfe.",
      },
      {
        title: "Liaison Saint-Tropez",
        text: "Village de Grimaud ⇄ Saint-Tropez, port et soirées, avec retour de nuit en toute sérénité.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Grimaud : Cannes, Nice, Monaco et au-delà, avec un chauffeur dédié.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Village de Grimaud",
        description:
          "Bourg médiéval perché, ruelles pavées et rue des Templiers.",
      },
      {
        name: "Château de Grimaud",
        description: "Ruines du château féodal dominant le golfe.",
      },
      {
        name: "Moulin Saint-Roch",
        description: "Ancien moulin à vent restauré au-dessus du village.",
      },
      {
        name: "Port Grimaud",
        description: "Cité lacustre en contrebas, la « Venise provençale ».",
      },
      {
        name: "Gare de Saint-Raphaël",
        description: "Gare TGV à environ 30-40 min, point de transfert.",
      },
      {
        name: "Aéroport de Nice",
        description: "Aéroport Nice Côte d’Azur, à environ 1h20-1h30.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour Grimaud et son golfe",
    subheading:
      "Du village médiéval perché à Port Grimaud, un service premium dans tout le golfe de Saint-Tropez.",
    paragraphs: [
      "Grimaud domine le golfe de Saint-Tropez depuis son éperon : village médiéval, ruines du château et moulins veillent sur Port Grimaud, la cité lacustre en contrebas. Ce relief et des stationnements éloignés du cœur piéton rendent un chauffeur particulièrement utile, qui vous dépose au plus près et revient sur simple appel.",
      "Le village n’a pas d’aéroport : la plupart des arrivées se font par Nice Côte d’Azur, à environ 1h20 à 1h30 de route selon le trafic estival. Nous calons l’heure de départ pour limiter l’attente, et proposons la gare TGV de Saint-Raphaël, à une trentaine de minutes, comme point de transfert alternatif.",
      "Sur place, un même chauffeur relie le village haut, Port Grimaud, les plages et Saint-Tropez, avec la souplesse attendue par la clientèle des villas en haute saison. Forfaits fixes définis à l’avance ou mise à disposition à l’heure : devis immédiat pour chaque trajet.",
    ],
    bullets: [
      "Dépose au plus près du village",
      "Transfert aéroport de Nice & gare de Saint-Raphaël",
      "Mise à disposition dans le golfe",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Grimaud",
    steps: [
      "Indiquez votre trajet (village, Port Grimaud, aéroport, gare) et vos horaires.",
      "Nous recommandons le véhicule et le forfait adaptés.",
      "Le chauffeur suit votre vol ou votre train et ajuste le départ.",
      "Transfert direct ou mise à disposition pour le golfe pendant votre séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Grimaud"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Grimaud ?",
      a: "Comptez environ 1h20 à 1h30 hors affluence, davantage lors des week-ends et pics de trafic estivaux du golfe. Nous adaptons l’heure de départ et suivons votre vol.",
    },
    {
      q: "Desservez-vous la gare TGV de Saint-Raphaël ?",
      a: "Oui, la gare de Saint-Raphaël se situe à environ 30 à 40 minutes de Grimaud ; c’est un point de transfert alternatif pratique pour les arrivées et départs en train.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Tropez ?",
      a: "Oui, Grimaud est tout proche de Saint-Tropez ; nous assurons les allers-retours vers le port, les restaurants et les soirées, avec retour de nuit.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En haute saison estivale, réservez au plus tôt : la demande est forte dans le golfe de Saint-Tropez. Nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale et aux séjours en villa.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes, van premium (Classe V), SUV et minibus selon le nombre de passagers et les bagages, adaptés à la longue distance depuis Nice ou Cannes.",
    },
    {
      q: "Peut-on rejoindre le cœur du village perché en voiture ?",
      a: "Le centre historique est en grande partie piéton et pentu. Le chauffeur vous dépose au plus près de l’entrée du village et vous récupère au même endroit, sans recherche de stationnement.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Saint-Tropez, tarifs et trajets",
      href: "/chauffeur/saint-tropez",
    },
    { label: "VTC à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location de voiture Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Grimaud",
    text: "Transfert aéroport de Nice, gare de Saint-Raphaël ou soirée à Saint-Tropez : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Grimaud — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Grimaud : transfert aéroport de Nice, gare de Saint-Raphaël, mise à disposition et liaison Saint-Tropez. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/grimaud",
  },
};

export default grimaud;
